import { serializeVideoMention } from "@ui/organisms/QuestionBox/promptMentionSerialization";
import type { QuestionBoxVideoMentionData } from "@ui/organisms/QuestionBox/QuestionBoxVideoMention";

/** Saved-answer handoff for a fresh Studio ask (session storage JSON). */
export type AskQuestionContextHandoff = {
	artifactId: string;
	answerId: string;
	/** Short label in the context row pill. */
	title: string;
	/** Longer copy for the context pill tooltip (e.g. the saved answer title). */
	description?: string;
};

/** Seeds tweak-view video context when opening a playbook from draft or library. */
export type AskQuestionTweakVideoHandoff = {
	playbookSlug: string;
	videos: QuestionBoxVideoMentionData[];
};

/** Payload stored per ask-page handoff slot (session storage). */
export type AskQuestionStashMap = {
	/** Prefilled question text (e.g. video mentions from library or clipboard). */
	prompt: string;
	/** Saved answer attached as the only context on a new conversation. */
	contextAnswer: AskQuestionContextHandoff;
	/** Video scope for the playbook tweak view opened via navigation handoff. */
	tweakPlaybookVideos: AskQuestionTweakVideoHandoff;
};

export type AskQuestionStashKind = keyof AskQuestionStashMap;

export type AskQuestionStashStorage = {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
	removeItem(key: string): void;
};

type AskQuestionStashSlot<K extends AskQuestionStashKind> = {
	key: string;
	serialize: (value: AskQuestionStashMap[K]) => string;
	parse: (raw: string) => AskQuestionStashMap[K] | null;
	isEmpty: (value: AskQuestionStashMap[K]) => boolean;
};

const parseContextAnswerHandoff = (
	raw: string,
): AskQuestionContextHandoff | null => {
	const trimmed = raw.trim();
	if (trimmed === "") {
		return null;
	}
	try {
		const parsed = JSON.parse(trimmed) as Partial<AskQuestionContextHandoff>;
		if (
			typeof parsed.artifactId === "string" &&
			parsed.artifactId.trim() !== ""
		) {
			const title =
				typeof parsed.title === "string" && parsed.title.trim() !== ""
					? parsed.title.trim()
					: "Saved answer";
			const description =
				typeof parsed.description === "string" &&
				parsed.description.trim() !== ""
					? parsed.description.trim()
					: undefined;
			return {
				artifactId: parsed.artifactId.trim(),
				answerId:
					typeof parsed.answerId === "string" ? parsed.answerId.trim() : "",
				title,
				...(description ? { description } : {}),
			};
		}
	} catch {
		// Legacy: stash held only the artifact id string.
	}
	return {
		artifactId: trimmed,
		answerId: "",
		title: "Saved answer",
	};
};

const ASK_QUESTION_STASH_SLOTS: {
	[K in AskQuestionStashKind]: AskQuestionStashSlot<K>;
} = {
	prompt: {
		key: "askgvt:ask-question:stash",
		serialize: (value) => value,
		parse: (raw) => {
			const trimmed = raw.trim();
			return trimmed === "" ? null : trimmed;
		},
		isEmpty: (value) => value.trim() === "",
	},
	tweakPlaybookVideos: {
		key: "askgvt:ask-question:tweak-playbook-videos",
		serialize: (handoff) =>
			JSON.stringify({
				playbookSlug: handoff.playbookSlug.trim(),
				videos: handoff.videos,
			}),
		parse: (raw) => {
			const trimmed = raw.trim();
			if (trimmed === "") {
				return null;
			}
			try {
				const parsed = JSON.parse(trimmed) as Partial<AskQuestionTweakVideoHandoff>;
				if (
					typeof parsed.playbookSlug !== "string" ||
					parsed.playbookSlug.trim() === "" ||
					!Array.isArray(parsed.videos)
				) {
					return null;
				}
				const videos = parsed.videos.filter(
					(video): video is QuestionBoxVideoMentionData =>
						typeof video === "object" &&
						video !== null &&
						typeof (video as QuestionBoxVideoMentionData).id === "string" &&
						(video as QuestionBoxVideoMentionData).id.trim() !== "",
				);
				if (videos.length === 0) {
					return null;
				}
				return {
					playbookSlug: parsed.playbookSlug.trim(),
					videos,
				};
			} catch {
				return null;
			}
		},
		isEmpty: (handoff) =>
			handoff.playbookSlug.trim() === "" || handoff.videos.length === 0,
	},
	contextAnswer: {
		key: "askgvt:ask-question:context-artifact",
		serialize: (handoff) =>
			JSON.stringify({
				artifactId: handoff.artifactId.trim(),
				answerId: handoff.answerId.trim(),
				title: handoff.title.trim() || "Saved answer",
				...(handoff.description?.trim()
					? { description: handoff.description.trim() }
					: {}),
			}),
		parse: parseContextAnswerHandoff,
		isEmpty: (handoff) => handoff.artifactId.trim() === "",
	},
};

/** Session storage keys for each handoff slot (tests, debugging). */
export const askQuestionStashKeys: {
	[K in AskQuestionStashKind]: string;
} = {
	prompt: ASK_QUESTION_STASH_SLOTS.prompt.key,
	contextAnswer: ASK_QUESTION_STASH_SLOTS.contextAnswer.key,
	tweakPlaybookVideos: ASK_QUESTION_STASH_SLOTS.tweakPlaybookVideos.key,
};

export type LibraryAskVideo = {
	id: string;
	title: string;
	thumbnailUrl: string | null;
};

/** Same-load memo so multiple consumers (e.g. loading shell + LibraryView) share one take. */
const takenThisLoad: Partial<{
	[K in AskQuestionStashKind]: AskQuestionStashMap[K] | null;
}> = {};

const defaultStorage = (): AskQuestionStashStorage | undefined => {
	if (typeof window === "undefined" || !window.sessionStorage) {
		return undefined;
	}
	return window.sessionStorage;
};

export function writeAskQuestionStashFor<K extends AskQuestionStashKind>(
	kind: K,
	value: AskQuestionStashMap[K],
	storage: AskQuestionStashStorage | undefined = defaultStorage(),
): void {
	const slot = ASK_QUESTION_STASH_SLOTS[kind];
	if (!storage || slot.isEmpty(value)) {
		return;
	}
	try {
		storage.setItem(slot.key, slot.serialize(value));
	} catch {
		// Private browsing, quota, or blocked storage.
	}
}

export function takeAskQuestionStashFor<K extends AskQuestionStashKind>(
	kind: K,
	storage: AskQuestionStashStorage | undefined = defaultStorage(),
): AskQuestionStashMap[K] | undefined {
	if (Object.hasOwn(takenThisLoad, kind)) {
		const cached = takenThisLoad[kind] as AskQuestionStashMap[K] | null;
		return cached ?? undefined;
	}
	if (!storage) {
		takenThisLoad[kind] = null as AskQuestionStashMap[K] | null;
		return undefined;
	}
	try {
		const value = storage.getItem(ASK_QUESTION_STASH_SLOTS[kind].key);
		storage.removeItem(ASK_QUESTION_STASH_SLOTS[kind].key);
		const parsed = (
			value ? ASK_QUESTION_STASH_SLOTS[kind].parse(value) : null
		) as AskQuestionStashMap[K] | null;
		takenThisLoad[kind] = parsed;
		return parsed ?? undefined;
	} catch {
		takenThisLoad[kind] = null as AskQuestionStashMap[K] | null;
		return undefined;
	}
}

export function mentionPromptFromQuestionBoxMentions(
	mentions: Iterable<QuestionBoxVideoMentionData>,
): string {
	return [...mentions].map(serializeVideoMention).join("");
}

export function mentionPromptFromLibraryVideos(
	videos: Iterable<LibraryAskVideo>,
): string {
	return mentionPromptFromQuestionBoxMentions(
		[...videos].map((video) => ({
			id: video.id,
			label: video.title,
			thumbnailUrl: video.thumbnailUrl,
			startTimeSec: null,
			endTimeSec: null,
		})),
	);
}

export function questionFromLocationSearch(
	search = typeof window === "undefined" ? "" : window.location.search,
): string {
	return new URLSearchParams(search).get("question")?.trim() ?? "";
}

export function resetAskQuestionStashForTests(): void {
	for (const kind of Object.keys(takenThisLoad) as AskQuestionStashKind[]) {
		delete takenThisLoad[kind];
	}
}
